// ===================
// SHARED TYPES
// ===================

import type { InventoryPartType, InventoryUnit } from './inventory';

export interface ComparisonChange {
    [metricKey: string]: number; // e.g. { orderVolume: 12.5, avgGrossMarginPct: -0.5 }
}

export type ReportingComparison =
    | { mode: 'rolling'; offsetDays?: number }
    | { mode: 'calendar'; period: 'month' | 'quarter' };

export type DomainType = 'location' | 'vendor' | 'insurance' | 'oem';

/** Vendor tab grouping: per-vendor (UI: "Total") or by org preference tier (UI: "By Preference"). */
export type VendorGroupBy = 'vendor' | 'preference';

// Metrics where a decrease is considered good (shown in green)
export const NEGATIVE_METRICS = [
    'missedOpportunity',
    'avgMissedOpportunity',
    'medianMissedOpportunity',
    'grossMarginLossFromPM',
    'pctPartsFlagged',
    'totalReturnCount',
    'totalReturnValue',
    'totalReturnPct',
    'totalCostVariance',
    'avgCostVariance',
    'medianCostVariance',
    'avgDeliveryVarianceDays',
    'medianDeliveryVarianceDays',
] as const;

// Metrics that are already percentages (show pp instead of % for changes)
export const PERCENTAGE_METRICS = [
    'avgGrossMargin',
    'avgGrossMarginUnweightedRo',
    'avgGrossMarginUnweightedLine',
    'avgGrossMarginWoPM',
    'medianGrossMargin',
    'medianGrossMarginWoPM',
    'avgGrossMarginPct',
    'medianGrossMarginPct',
    'avgGrossMarginPctWoPM',
    'medianGrossMarginPctWoPM',
    'grossMarginLossFromPM',
    'pctAutoOrdered',
    'pctAutoOrderEnabled',
    'pctPartsFlagged',
    'partListPricePctOfOE',
    'pctOemVolume',
    'pctOemCount',
    'pctLkqVolume',
    'pctLkqCount',
    'pctAftermarketVolume',
    'pctAftermarketCount',
    'pctOfTotalVolume',
    'pctOfTotalCount',
    'pctOfTotalProfit'
] as const;

// All 17+ metrics supported across the system
export type MetricType =
    | 'orderVolume'           // Order Volume ($)
    | 'orderCost'             // Order Cost ($)
    | 'orderCount'            // Order Volume (Qty)
    | 'partsOrdered'          // Parts Ordered (Qty)
    | 'avgListPrice'          // Average List Price ($)
    | 'medianListPrice'       // Median List Price ($)
    | 'avgListPriceWoPM'      // Avg List Price w/o PM ($)
    | 'medianListPriceWoPM'   // Median List Price w/o PM ($)
    | 'avgOrderSize'          // Average Order Size ($)
    | 'medianOrderSize'       // Median Order Size ($)
    | 'avgGrossMarginPct'     // Avg Gross Margin (%)
    | 'medianGrossMarginPct'  // Median Gross Margin (%)
    | 'avgGrossMarginPctWoPM' // Avg GM w/o PM (%)
    | 'medianGrossMarginPctWoPM' // Median GM w/o PM (%)
    | 'grossMarginLossFromPM' // GM Loss from PM (%)
    | 'missedOpportunity'     // Missed Opportunity ($)
    | 'pctAutoOrdered'        // % Auto Ordered
    | 'pctAutoOrderEnabled'   // % Auto Order Enabled
    | 'grossProfit';          // Gross Profit ($)

// Base request for all reporting endpoints
export interface BaseReportingRequest {
    startDate: string;   // 'YYYY-MM-DD'
    endDate: string;     // 'YYYY-MM-DD'
    locationId?: string;
    /** One or more shop locations. Omit for every location in the user's scope. */
    locationIds?: string[];
    preset?: string;     // e.g. 'last_7_days', 'last_month'
    includeComparison?: boolean;
    comparison?: ReportingComparison;
    excludeEvs?: boolean;
}

// Legacy request type (for backwards compatibility)
export interface ReportingRequest extends BaseReportingRequest {
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
}

// ===================
// OVERVIEW SUMMARY
// ===================

export interface OverviewSummaryResponse {
    totalOrders: number;
    totalVolume: number;
    /** Total buy dollars paid to suppliers for parts ordered. */
    totalOrderCost: number;
    /** Total list volume using base list from the selected option (not price-match adjustments). */
    totalVolumeWoPM: number;
    totalParts: number;
    grossProfit: number;
    avgGrossMargin: number;
    /** Mean of per-RO GM%; equal weight per order (compare to list-weighted avgGrossMargin). */
    avgGrossMarginUnweightedRo: number;
    /** Mean of per-line GM%; equal weight per part line (diagnostic). */
    avgGrossMarginUnweightedLine: number;
    /** Dollar-weighted GM using base list from the selected option (not price-match adjustments). */
    avgGrossMarginWoPM: number;
    avgListPrice: number;
    avgOrderSize: number;
    medianGrossMargin: number;
    /** Median of per-order GM% using base list (not price-match adjustments). */
    medianGrossMarginWoPM: number;
    medianListPrice: number;
    medianOrderSize: number;
    prior?: Omit<OverviewSummaryResponse, 'prior' | 'change'>;
    change?: ComparisonChange;
}

// ===================
// INVOICING SUMMARY
// ===================

export interface TopFlaggedVendorTypeRow {
    vendorName: string;
    invoiceType: 'received' | 'returned' | 'return_slip' | 'sublet';
    invoiceCount: number;
    flagCount: number;
    /** Percent of invoices in the pair that are still flagged or were manually submitted. */
    flagRate: number;
    /** Percent auto-submitted among processed invoices, excluding duplicates. */
    autoSubmissionRate: number;
}

export interface InvoicingSummaryResponse {
    /** Count of processed invoices (submitted + manually_submitted; excludes still-flagged). */
    processedInvoiceCount: number;
    /** Σ orderTotal of processed invoices (return slips $0). */
    totalInvoicesValue: number;
    /** Average # of unique parts per processed invoice. */
    avgPartCount: number;
    /** Median # of unique parts per processed invoice. */
    medianPartCount: number;
    /** Average orderTotal per processed received/sublet invoice. */
    avgInvoiceValue: number;
    /** Median orderTotal per processed received/sublet invoice. */
    medianInvoiceValue: number;
    /** Still-flagged (non-archived) + manually submitted. */
    invoiceFlags: number;
    /** Σ orderTotal of flag-metric rows (return slips $0). */
    invoiceFlagsValue: number;
    /** submitted / (submitted + manually_submitted) * 100, excluding duplicates. */
    autoSubmissionRate: number;
    /** Count of processed return (credit) invoices. */
    creditsCount: number;
    /** Σ orderTotal of processed returned (credit) invoices. */
    creditsValue: number;
    /** Distinct `supplierText` in the current window (before vendor filter; after invoice-type filter). */
    vendorOptions: string[];
    /** Per-vendor invoice + variance rollups (matched parts that reached accounting). */
    byVendor: InvoicingVendorSummary[];
    /** Flagged-upload mix and Stripe billing mix. Present on the current summary response. */
    composition?: InvoicingCompositionResponse;
    /** Vendor and invoice-type pairs with the highest flag rate. Each has at least 10 invoices. */
    topFlaggedVendorTypes?: TopFlaggedVendorTypeRow[];
    /** Another page of five ranked slices exists after this response. */
    topFlaggedVendorTypesHasMore?: boolean;
    prior?: Omit<InvoicingSummaryResponse, 'prior' | 'change' | 'vendorOptions' | 'byVendor' | 'composition' | 'topFlaggedVendorTypes' | 'topFlaggedVendorTypesHasMore'> & {
        byVendor: Array<Omit<InvoicingVendorSummary, 'prior' | 'change'>>;
    };
    change?: ComparisonChange;
}

/** Numeric-only prior payload on a summary vendor row (identity fields dropped). */
export interface InvoicingVendorSummaryPrior {
    invoiceCount: number;
    totalInvoicesValue: number;
    totalCostVariance: number;
    avgCostVariance: number;
    /** Omitted when prior sample was null. */
    avgDeliveryVarianceDays?: number;
    deliveryVarianceDaysSum: number;
    deliveryVariancePartCount: number;
    matchedPartCount: number;
}

export interface InvoicingVendorSummary {
    /** Supplier id, or `name:<lowercased supplierText>`. */
    vendorId: string;
    vendorName: string;
    /** Processed invoices attributed to vendor. */
    invoiceCount: number;
    /** USD; return slips contribute $0. */
    totalInvoicesValue: number;
    /** USD sum over matched order parts. */
    totalCostVariance: number;
    /** USD per matched order part; 0 if none. */
    avgCostVariance: number;
    /** Days; null if no delivery samples. */
    avgDeliveryVarianceDays: number | null;
    /** Sum of part delivery variances (days); 0 if no samples. */
    deliveryVarianceDaysSum: number;
    /** Parts with a delivery variance (subset of matchedPartCount). */
    deliveryVariancePartCount: number;
    /** Matched order parts in cost variance. */
    matchedPartCount: number;
    prior?: InvoicingVendorSummaryPrior;
    change?: ComparisonChange;
}

/** Flagged-upload mix and Stripe billing mix for the Invoicing tab doughnuts. */
export interface InvoicingCompositionResponse {
    flagged: {
        total: number;
        theirFault: number;
        neededFlags: number;
        match: number;
    };
    stripe: {
        total: number;
        postedOpen: number;
        postedArchived: number;
        flaggedOpen: number;
        flaggedArchived: number;
    };
}

// ===================
// INVOICING TIMESERIES
// ===================

export type InvoiceTypeKey = 'received' | 'returned' | 'return_slip' | 'sublet';

export type FlagTypeKey =
    | 'ro' | 'match' | 'duplicate' | 'math' | 'vendor'
    | 'specialVendor' | 'locationMismatch' | 'stale' | 'price' | 'quantity' | 'amount' | 'error';

export interface InvoicingTimeseriesSegment {
    /** Active processed invoice count (submitted + manually_submitted; excludes still-flagged). */
    count: number;
    /** Processed invoice value (submitted + manually_submitted; excludes still-flagged). */
    value: number;
}

/** Per-location segment carrying every invoicing metric for that location in the bucket. */
export interface InvoicingLocationSegment extends InvoicingTimeseriesSegment {
    submittedCount: number;
    manuallySubmittedCount: number;
    flagValue: number;
    /** Duplicate-excluded counters behind autoSubmissionRate (for exact aggregation). */
    rateSubmitted: number;
    rateDenominator: number;
    autoSubmissionRate: number;
}

/** Per-vendor segment for invoicing timeseries (variance + invoice volume). */
export interface InvoicingVendorSegment extends InvoicingTimeseriesSegment {
    costVarianceSum: number;
    deliveryVarianceDaysSum: number;
    deliveryVariancePartCount: number;
    matchedPartCount: number;
    avgCostVariance: number;
    avgDeliveryVarianceDays: number | null;
    vendorName: string;
}

export interface InvoicingTimeseriesDataPoint {
    date: string;
    priorDate?: string;
    priorEndDate?: string;
    /** Active processed invoice count in the bucket. */
    invoiceCount: number;
    /** Σ orderTotal of active processed invoices in the bucket. */
    value: number;
    /** Auto-submitted (no flag) invoice count. */
    submittedCount: number;
    /** Manually-submitted (flagged then pushed) invoice count = Invoice Flags total. */
    manuallySubmittedCount: number;
    /** Σ orderTotal of the manually-submitted (flagged) invoices. */
    flagValue: number;
    /** submitted / (submitted + manually_submitted) * 100, excluding duplicate submissions. */
    autoSubmissionRate: number;
    /** Duplicate-excluded counters behind autoSubmissionRate (for exact aggregation). */
    rateSubmitted: number;
    rateDenominator: number;
    byInvoiceType: Record<InvoiceTypeKey, InvoicingTimeseriesSegment>;
    /** Flag occurrences by flag type across manually-submitted invoices. */
    byFlagType: Record<FlagTypeKey, InvoicingTimeseriesSegment>;
    byLocation: Record<string, InvoicingLocationSegment>;
    /** Always present; {} if none. */
    byVendor: Record<string, InvoicingVendorSegment>;
    prior?: Omit<InvoicingTimeseriesDataPoint, 'date' | 'priorDate' | 'priorEndDate' | 'prior' | 'change'>;
    /** Percentage / pp change from prior period (backend-authored when comparison is requested). */
    change?: ComparisonChange;
}

export interface InvoicingTimeseriesResponse {
    data: InvoicingTimeseriesDataPoint[];
    /** Bare prior-period buckets (no nested comparison). Nested prior/change live on data[]. */
    priorData?: Array<Omit<InvoicingTimeseriesDataPoint, 'priorDate' | 'priorEndDate' | 'prior' | 'change'>>;
    granularity: string;
    comparison?: ReportingComparison;
    /** Locations present in the range, sorted by total value desc (for stable stacking + colors). */
    locations: Array<{ id: string; name: string }>;
}

// ===================
// OVERVIEW TIMESERIES
// ===================

export interface TimeseriesRequest extends BaseReportingRequest {
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
}

export interface TimeseriesDataPoint {
    date: string;
    priorDate?: string;
    priorEndDate?: string;

    // Volume metrics
    orderCount: number;
    orderVolume: number;
    /** Pre-price-match list dollars (base listPrice), mirrors summary totalVolumeWoPM. */
    orderVolumeWoPM?: number;
    orderCost: number;
    partsOrdered: number;

    // List Price metrics
    avgListPrice: number;
    medianListPrice: number;
    avgListPriceWoPM: number;
    medianListPriceWoPM: number;

    // Order Size metrics
    avgOrderSize: number;
    medianOrderSize: number;

    // Gross Margin metrics (renamed from grossMarginPct)
    avgGrossMarginPct: number;
    medianGrossMarginPct: number;
    avgGrossMarginPctWoPM: number;
    medianGrossMarginPctWoPM: number;
    grossMarginLossFromPM: number;

    // Part List Price % of OE
    partListPricePctOfOE: number;

    // Profit metrics
    grossProfit: number;
    grossProfitWoPM?: number;

    // Missed Opportunity
    missedOpportunity: number;
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;

    // Auto Order metrics
    pctPartsFlagged: number;             // % of parts flagged

    // Part type mix metrics
    pctOemVolume: number;
    pctOemCount: number;
    pctLkqVolume: number;
    pctLkqCount: number;
    pctAftermarketVolume: number;
    pctAftermarketCount: number;

    // Per-marketplace breakdown of the same metrics, keyed by source
    // (e.g. 'partstrader', 'ccc', 'opstrax'). Used for stacked/clustered
    // bar charts segmented by marketplace. Sole source may be an inherited
    // reference (metrics ≡ parent point) until the client rehydrates.
    bySource?: Record<string, TimeseriesSourceItem>;

    // Prior period data (embedded by backend when comparison is requested)
    prior?: Partial<Omit<TimeseriesDataPoint, 'date' | 'priorDate' | 'priorEndDate' | 'prior' | 'change'>>;

    // Change data (percentage change from prior period)
    change?: ComparisonChange;
}

export type TimeseriesSourceMetrics = Omit<
    TimeseriesDataPoint,
    'date' | 'priorDate' | 'bySource' | 'prior' | 'change'
    | 'pctPartsFlagged'
>;

/** Sole marketplace source: metrics ≡ parent timeseries point. */
export interface TimeseriesSourceReference {
    id: string;
    name: string;
    metricsInheritedFromParent: true;
}

export type TimeseriesSourceItem = TimeseriesSourceMetrics | TimeseriesSourceReference;

export interface OverviewTimeseriesResponse {
    data: TimeseriesDataPoint[];
    granularity: string;
    comparison?: ReportingComparison;
}

// ===================
// OVERVIEW RANKINGS
// ===================

export interface RankingsRequest extends BaseReportingRequest {
    limit?: number;
}

export interface RankingItem {
    id: string;
    name: string;

    // Volume metrics
    orderCount: number;
    /** Distinct order IDs that contributed to this ranking (used for auditing drilldown). */
    orderIds?: string[];
    orderVolume: number;
    orderCost: number;
    partsOrdered: number;

    // Gross Margin metrics
    avgGrossMarginPct: number;
    medianGrossMarginPct: number;
    avgGrossMarginPctWoPM: number;
    medianGrossMarginPctWoPM: number;
    grossMarginLossFromPM: number;

    // Part List Price % of OE (for OEM parts: adjustedListPrice / listPrice * 100)
    partListPricePctOfOE: number;

    // Profit metrics
    grossProfit: number;

    // Missed Opportunity
    missedOpportunity: number;
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;

    // Percentage of total (for pie charts)
    pctOfTotalVolume: number;
    pctOfTotalVolumeWoPM?: number;
    pctOfTotalCount: number;
    pctOfTotalProfit: number;

    // Vendor-specific (only for vendors array)
    avgDiscount?: number;
    medianDiscount?: number;

    // Per-marketplace breakdown, keyed by source (e.g. 'partstrader', 'ccc').
    bySource?: Record<string, RankingItem>;

    // Prior period data (optional)
    prior?: Omit<RankingItem, 'id' | 'name' | 'prior'>;

    // Change data
    change?: ComparisonChange;
}

export interface OverviewRankingsResponse {
    locations: RankingItem[];
    vendors: RankingItem[];
    oems: RankingItem[];       // Renamed from 'brands'
    insurance: RankingItem[];
    partTypes: RankingItem[];  // NEW - Part type breakdown
}

export type OverviewBundleSection = 'summary' | 'timeseries' | 'rankings';

export interface OverviewBundleResponse {
    source: 'rollup';
    /** @deprecated Prefer excludeEvsOverlay on combined responses. */
    excludeEvsSections?: OverviewBundleSection[];
    /** Present when this cache entry was built from selective `sections` fetches. */
    sectionsOnly?: true;
    sections?: OverviewBundleSection[];
    summary?: OverviewSummaryResponse;
    timeseries?: OverviewTimeseriesResponse;
    rankings?: OverviewRankingsResponse;
}

/** Incremental lazy fetch — only sections using the excludeEvs rollup slice. */
export interface OverviewBundlePartialResponse {
    source: 'rollup';
    excludeEvsSectionsOnly: true;
    excludeEvsSections: OverviewBundleSection[];
    summary?: OverviewSummaryResponse;
    timeseries?: OverviewTimeseriesResponse;
    rankings?: OverviewRankingsResponse;
}

/** Incremental lazy fetch — selective sections from the `all` rollup slice. */
export interface OverviewBundleSectionsOnlyResponse {
    source: 'rollup';
    sectionsOnly: true;
    sections: OverviewBundleSection[];
    summary?: OverviewSummaryResponse;
    timeseries?: OverviewTimeseriesResponse;
    rankings?: OverviewRankingsResponse;
}

/** Nested overlay on a combined full-bundle response. */
export interface OverviewBundleExcludeEvsOverlay {
    excludeEvsSections: OverviewBundleSection[];
    summary?: OverviewSummaryResponse;
    timeseries?: OverviewTimeseriesResponse;
    rankings?: OverviewRankingsResponse;
}

/**
 * Full `all` bundle plus excludeEvs overlay from one dual-cube server load.
 * Returned when excludeEvsSections is set and excludeEvsSectionsOnly is not.
 */
export interface OverviewBundleCombinedResponse {
    source: 'rollup';
    summary: OverviewSummaryResponse;
    timeseries: OverviewTimeseriesResponse;
    rankings: OverviewRankingsResponse;
    excludeEvsOverlay: OverviewBundleExcludeEvsOverlay;
}

// ===================
// DOMAIN STATS (Unified for Location/Vendor/Insurance/OEM)
// ===================

export interface DomainStatsRequest extends BaseReportingRequest {
    domain: DomainType;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    preset?: string;
    /** Vendor tab only: group rows by supplier or by preference tier. */
    groupBy?: VendorGroupBy;
    /**
     * When false, omit per-marketplace `bySource` on each item.
     * Default true on the API when omitted.
     */
    includeBySource?: boolean;
}

/** Single-source current: metrics ≡ parent (omit duplicate payload). */
export interface DomainStatSourceReference {
    id: string;
    name: string;
    metricsInheritedFromParent: true;
}

/** Prior-only marketplace: current 0, prior ≡ parent.prior. */
export interface DomainStatPriorSourceReference {
    id: string;
    name: string;
    priorInheritedFromParent: true;
}

/** Wire or rehydrated marketplace breakdown entry. */
export type DomainStatSourceItem =
    | DomainStatItem
    | DomainStatSourceReference
    | DomainStatPriorSourceReference;

export interface DomainStatItem {
    id: string;
    name: string;
    orderCount: number;
    /** Distinct order IDs that contributed to this stat (used for auditing drilldown). */
    orderIds?: string[];
    orderVolume: number;
    /** Pre-price-match list dollars (base listPrice), mirrors summary totalVolumeWoPM. */
    orderVolumeWoPM?: number;
    orderCost: number;
    partsOrdered: number;
    grossProfit: number;
    /** Gross profit using pre-price-match list dollars. */
    grossProfitWoPM?: number;
    missedOpportunity: number;
    /** Distinct orders with ≥1 MO-eligible part line (denominator for avg missed opportunity). */
    moEligibleOrderCount: number;

    // Average metrics
    avgListPrice: number;
    avgListPriceWoPM: number;
    avgOrderSize: number;
    avgGrossMarginPct: number;
    avgGrossMarginPctWoPM: number;

    // Median metrics
    medianListPrice: number;
    medianListPriceWoPM: number;
    medianOrderSize: number;
    medianGrossMarginPct: number;
    medianGrossMarginPctWoPM: number;

    // Suggested Thresholds (OEM). Name is a legacy error: this is the 20th percentile of
    // per-order GM% (80% of orders beat it), not the 90th.
    p90GrossMarginPct: number;

    // PM impact
    grossMarginLossFromPM: number;

    // Missed Opportunity
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;

    // Part List Price % of OE
    partListPricePctOfOE: number;

    // Raw OEM totals for accurate aggregate calculation (internal use)
    totalFinalListPriceForOEM?: number;
    totalOriginalListPriceForOEM?: number;

    // Auto order
    pctAutoOrdered: number;
    pctAutoOrderEnabled: number;
    pctPartsFlagged: number;

    // Whether this entity is eligible for missed-opportunity metrics (partstraderRequired insurer)
    missedOpportunityEligible?: boolean;

    // Vendor-specific: Agreed discount from organization settings
    agreedDiscount?: number | null;

    // Part value
    avgPartValue: number;
    medianPartValue: number;

    // Quotes
    totalQuotes: number;
    avgQuotesPerOrder: number;

    // Supplier ETA changes (newDelivery vs delivery), in days. Positive = pushed later.
    partsWithEtaChange?: number;
    avgEtaChangeDays?: number;
    medianEtaChangeDays?: number;

    // Invoice cost / delivery variance (matched parts that reached accounting)
    totalCostVariance: number;
    avgCostVariance: number;
    medianCostVariance: number;
    avgDeliveryVarianceDays: number;
    medianDeliveryVarianceDays: number;
    deliveryVariancePartCount?: number;
    /** % of ordered parts with a finalized invoice match (0–100). */
    pctPartsInvoiced: number;

    // Vendor quote rate (vendor domain + groupBy vendor only; omitted when opportunities are 0)
    quoteOpportunities?: number;
    quotesSubmitted?: number;
    quoteRate?: number;

    // Returns
    totalReturnCount: number;
    totalReturnValue: number;
    totalReturnPct: number;

    // Part-type breakdowns (% of total for this entity)
    pctOemVolume: number;
    pctOemCount: number;
    pctLkqVolume: number;
    pctLkqCount: number;
    pctAftermarketVolume: number;
    pctAftermarketCount: number;

    // Per-marketplace breakdown, keyed by source (e.g. 'partstrader', 'ccc').
    // Present for location/vendor/oem. Wire may send slim identity markers;
    // rehydrateDomainStatItem expands them to full DomainStatItems.
    bySource?: Record<string, DomainStatSourceItem>;

    // Prior period data
    prior?: Partial<DomainStatItem>;

    // Change data
    change?: ComparisonChange;
}

// Type helpers for metric constants
export type NegativeMetric = typeof NEGATIVE_METRICS[number];
export type PercentageMetric = typeof PERCENTAGE_METRICS[number];

export interface DomainStatsTotals {
    orderCount: number;
    orderVolume: number;
    orderVolumeWoPM?: number;
    orderCost: number;
    partsOrdered: number;
    grossProfit: number;
    grossProfitWoPM?: number;
    missedOpportunity: number;
    moEligibleOrderCount: number;
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;
    partListPricePctOfOE: number;
    avgListPrice: number;
    avgListPriceWoPM: number;
    avgOrderSize: number;
    avgGrossMarginPct: number;
    avgGrossMarginPctWoPM: number;
    medianListPrice: number;
    medianListPriceWoPM: number;
    medianOrderSize: number;
    medianGrossMarginPct: number;
    medianGrossMarginPctWoPM: number;
    // Suggested Thresholds: 20th percentile of per-order GM% (80% of orders beat it).
    p90GrossMarginPct: number;
    grossMarginLossFromPM: number;
    pctAutoOrdered: number;
    pctAutoOrderEnabled: number;

    // Part value
    avgPartValue: number;
    medianPartValue: number;

    // Quotes
    totalQuotes: number;
    avgQuotesPerOrder: number;

    // Supplier ETA changes (newDelivery vs delivery), in days
    partsWithEtaChange?: number;
    avgEtaChangeDays?: number;
    medianEtaChangeDays?: number;

    // Invoice cost / delivery variance
    totalCostVariance: number;
    avgCostVariance: number;
    medianCostVariance: number;
    avgDeliveryVarianceDays: number;
    medianDeliveryVarianceDays: number;
    deliveryVariancePartCount: number;
    pctPartsInvoiced: number;

    // Returns
    totalReturnCount: number;
    totalReturnValue: number;
    totalReturnPct: number;

    // Part-type breakdowns
    pctOemVolume: number;
    pctOemCount: number;
    pctLkqVolume: number;
    pctLkqCount: number;
    pctAftermarketVolume: number;
    pctAftermarketCount: number;

    // Change data
    change?: ComparisonChange;
}

export interface DomainStatsResponse {
    items: DomainStatItem[];
    totals: DomainStatsTotals;
    domain: DomainType;
}

// ===================
// DOMAIN TIMESERIES (Time-series data per domain entity)
// ===================

export interface DomainTimeseriesRequest extends BaseReportingRequest {
    domain: DomainType;
    /** Filter to one entity (vendorId, insuranceId, etc.). */
    domainId?: string;
    /**
     * Filter to multiple entities for compare (max 5).
     * When set, overrides `domainId`.
     */
    domainIds?: string[];
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    /** Vendor tab only: group series by supplier or by preference tier. */
    groupBy?: VendorGroupBy;
}

// Domain timeseries data point - includes ALL metrics
export interface DomainTimeseriesDataPoint {
    date: string;
    priorDate?: string;
    priorEndDate?: string;
    domainId: string;
    domainName: string;

    // Volume metrics
    orderCount: number;
    orderVolume: number;
    orderVolumeWoPM?: number;
    orderCost: number;
    partsOrdered: number;

    // List Price metrics
    avgListPrice: number;
    medianListPrice: number;
    avgListPriceWoPM: number;
    medianListPriceWoPM: number;

    // Order Size metrics
    avgOrderSize: number;
    medianOrderSize: number;

    // Gross Margin metrics
    avgGrossMarginPct: number;
    medianGrossMarginPct: number;
    // Suggested Thresholds: 20th percentile of per-order GM% (80% of orders beat it).
    p90GrossMarginPct?: number;
    avgGrossMarginPctWoPM: number;
    medianGrossMarginPctWoPM: number;
    grossMarginLossFromPM: number;

    // Part List Price % of OE
    partListPricePctOfOE: number;

    // Profit metrics
    grossProfit: number;
    grossProfitWoPM?: number;

    // Missed Opportunity
    missedOpportunity: number;
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;

    // Auto Order metrics
    pctAutoOrdered: number;
    pctAutoOrderEnabled: number;
    pctPartsFlagged: number;             // % of parts flagged

    // Return metrics (parts marked returned on their supplier order)
    totalReturnCount: number;
    totalReturnValue: number;
    totalReturnPct: number;

    // Supplier ETA changes (newDelivery vs delivery), in days
    partsWithEtaChange?: number;
    avgEtaChangeDays?: number;
    medianEtaChangeDays?: number;

    // Invoice cost / delivery variance (always set on the rollup adapter path)
    totalCostVariance: number;
    avgCostVariance: number;
    medianCostVariance: number;
    avgDeliveryVarianceDays: number;
    medianDeliveryVarianceDays: number;
    pctPartsInvoiced: number;

    // Vendor quote rate (vendor domain + groupBy vendor only; omitted when opportunities are 0)
    quoteOpportunities?: number;
    quotesSubmitted?: number;
    quoteRate?: number;

    // Prior period data (embedded by backend when comparison is requested)
    prior?: Partial<Omit<DomainTimeseriesDataPoint, 'date' | 'priorDate' | 'priorEndDate' | 'domainId' | 'domainName' | 'prior' | 'change'>>;

    // Change data (percentage change from prior period)
    change?: ComparisonChange;
}

/** Backend aggregateByDate/aggregatePriorByDate shape — no vendor-score variance fields. */
export type DomainTimeseriesAggregate = Pick<DomainTimeseriesDataPoint,
    | 'orderCount' | 'orderVolume' | 'orderVolumeWoPM' | 'orderCost' | 'partsOrdered'
    | 'grossProfit' | 'grossProfitWoPM' | 'missedOpportunity'
    | 'totalReturnCount' | 'totalReturnValue' | 'totalReturnPct'
    | 'partsWithEtaChange' | 'avgEtaChangeDays' | 'medianEtaChangeDays'
    | 'avgListPrice' | 'medianListPrice' | 'avgListPriceWoPM' | 'medianListPriceWoPM'
    | 'avgOrderSize' | 'medianOrderSize'
    | 'avgGrossMarginPct' | 'medianGrossMarginPct' | 'avgGrossMarginPctWoPM'
    | 'medianGrossMarginPctWoPM' | 'grossMarginLossFromPM' | 'p90GrossMarginPct'
    | 'avgMissedOpportunity' | 'medianMissedOpportunity' | 'partListPricePctOfOE'
    | 'pctAutoOrdered' | 'pctAutoOrderEnabled' | 'pctPartsFlagged'
>;

export interface DomainTimeseriesResponse {
    data: DomainTimeseriesDataPoint[];
    granularity: string;
    domain: DomainType;
    domainId?: string;
    /** Weighted/recomputed current metrics across all entities, keyed by bucket date. */
    aggregateByDate: Record<string, DomainTimeseriesAggregate>;
    aggregatePriorByDate?: Record<string, DomainTimeseriesAggregate>;
}

// ===================
// AUDITING STATS (with Order/Part views)
// ===================

export interface AuditingRequest extends BaseReportingRequest {
    insuranceId?: string;
    oemId?: string;
    viewMode?: 'orders' | 'parts';
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    minDaysActive?: number;
    maxDaysActive?: number;
    preset?: string;
}

export interface AutomationStats {
    pctOrdersPlaced: number; // Part-level: % of parts auto-ordered in auto-order enabled orders
    pctFullyAutoOrdered: number; // Order-level: % of auto-order enabled orders that were fully auto-ordered
    pctAutoOrderEnabled: number;
    pctPartsFlagged: number;
    totalOrders: number;
    totalAutoOrderEnabled: number;
    totalAutoOrdered: number; // Part-level count (derived from ratio)
    totalFullyAutoOrdered: number; // Order-level count
    totalFlaggedParts: number;
    totalPartsInAutoOrders: number;
    totalPartsWithOriginalSelected: number;
    totalParts: number;
}

export interface AutomationByLocation {
    locationId: string;
    locationName: string;
    totalOrders: number;
    totalParts: number;
    pctOrdersPlaced: number; // Part-level: % of parts auto-ordered in auto-order enabled orders
    pctFullyAutoOrdered: number; // Order-level: % of auto-order enabled orders that were fully auto-ordered
    pctAutoOrderEnabled: number;
    pctPartsFlagged: number;
    totalPartsInAutoOrders: number;
    totalPartsWithOriginalSelected: number;
    avgDaysActive: number;
    prior?: Partial<AutomationByLocation>;
}

// Order-level audit item for table view
export interface OrderAuditItem {
    orderId: string;
    roNumber: string;
    customerName: string;
    locationName: string;
    insuranceName: string | null;
    oemName: string | null;
    daysActive: number;
    totalParts: number;
    flaggedParts: number;
    isAutoOrderEnabled: boolean;
    wasAutoOrdered: boolean;
    status: string;
    timeCreated: string;
    lastUpdated: string;
}

// Part-level audit item for table view
export interface PartAuditItem {
    partId: string;
    orderId: string;
    roNumber: string;
    partTitle: string;
    partNumber: string;
    status: string;
    isFlagged: boolean;
    flagReason: string | null;
    listPrice: number;
    buyPrice: number;
    locationName: string;
    vendorName: string | null;
    daysActive: number;
    totalParts?: number; // Added to match usage in some contexts if needed
}

// ===================
// AUDITING TABLE TYPES (Extended for Auditing Tab)
// ===================

// Extended Order audit item for the Auditing tab table
// Contains raw totals - frontend calculates derived values (grossMarginPct)
export interface AuditingOrderItem {
    orderId: string;
    roNumber: string;
    writerNames?: string[];
    // Vehicle info
    vin: string | null;
    vehicleYear: number | null;
    vehicleMake: string | null;
    vehicleModel: string | null;
    // Insurance
    insuranceId: string | null;
    insuranceName: string | null;
    // Location
    locationId: string;
    locationName: string;
    // Dates
    dateStarted: string;
    lastUpdated: string;
    daysActive: number;
    // Parts counts
    partsOrdered: number;
    totalPartsForOrdering: number;
    partsQuoted: number;
    totalQuotes: number;
    partsConfirmed: number;
    partsExcluded: number;
    partsReturnedCancelled: number;
    // Raw financial totals (frontend calculates derived values)
    totalListPrice: number;
    totalBuyPrice: number;
    grossProfit: number;
    totalMissedOpp: number;
    totalVendors: number;
    /** USD sum of matched part cost variances; 0 if none. */
    totalCostVariance: number;
    /** Days; null if no delivery samples. */
    avgDeliveryVarianceDays: number | null;
    /** Count of ordered parts with a finalized invoice match. */
    partsInvoiced: number;
}

// Extended Part audit item for the Auditing tab table
export interface AuditingPartItem {
    partId: string;
    orderId: string;
    roNumber: string;
    writerNames?: string[];
    // Vehicle
    vin: string | null;
    vehicleYear: number | null;
    vehicleMake: string | null;
    vehicleModel: string | null;
    vehicleYmm: string | null;
    // Insurance
    insuranceId: string | null;
    insuranceName: string | null;
    // Location
    locationId: string;
    locationName: string;
    // Part details
    partName: string;
    partType: string | null;
    /** Estimate / order-part quantity (line qty from EMS). */
    quantity: number;
    // Vendor
    vendorId: string | null;
    vendorName: string | null;
    // Delivery
    deliveryDays: number | null;
    deliveryString: string | null;
    // Pricing (already line totals — do not multiply by quantity)
    originalListPrice: number;
    listPrice: number;
    buyPrice: number;
    partListPricePctOfOE: number | null;
    isPriceMatched: boolean;
    grossMargin: number; // In dollars
    grossMarginPct: number;
    missedOpportunity: number;
    quoteSource: string | null;
    dateStarted: string;
    lastUpdated: string;
    daysActive: number;
    quotedOptionsCount: number;
    /** True when the part has a finalized invoice match. */
    isInvoiced: boolean;
    /** USD; null if not invoiced. */
    costVariance: number | null;
    /** Days; null if not invoiced or no PO delivery promise. */
    deliveryVarianceDays: number | null;
    /** USD; null if not invoiced. */
    invoicedCost: number | null;
    /** ISO-8601; null if not invoiced. */
    invoiceDate: string | null;
    /**
     * ISO-formatted with "Z", but the value is location-local wall time (not true UTC).
     * null when not invoiced OR no PO ETA.
     */
    finalEta: string | null;
}

// Request for auditing orders endpoint (server-side filtering/sorting/pagination)
export interface AuditingOrdersRequest extends BaseReportingRequest {
    // Server-side filters to reduce data
    locationIds?: string[];
    insuranceIds?: string[];
    makes?: string[];
    /** Explicit order IDs (e.g. from pivot drilldown). */
    orderIds?: string[];
    /** Server pagination size (often pageSize + 1 to detect hasNextPage). */
    limit?: number;
    /** Server pagination offset. */
    offset?: number;
    /** Server-side free-text search. */
    searchQuery?: string;
    /** Server-side sort column key. */
    sortBy?: string;
    /** Server-side sort direction. */
    sortOrder?: 'asc' | 'desc';
    /** Server-side range filters by column key. */
    rangeFilters?: Record<string, { min?: string; max?: string; }>;
}

// Request for auditing parts endpoint (server-side filtering/sorting/pagination)
export interface AuditingPartsRequest extends BaseReportingRequest {
    // Filters (applied server-side)
    locationIds?: string[];
    insuranceIds?: string[];
    makes?: string[];
    partTypes?: string[];
    vendors?: string[];
    /** Explicit order IDs (e.g. from pivot drilldown). */
    orderIds?: string[];
    /** Server pagination (1-indexed page). */
    page?: number;
    /** Server pagination size. */
    pageSize?: number;
    /** Server-side free-text search. */
    searchQuery?: string;
    /** Server-side sort column key. */
    sortBy?: string;
    /** Server-side sort direction. */
    sortOrder?: 'asc' | 'desc';
    /** Server-side numeric range filters by column key. */
    rangeFilters?: Record<string, { min?: string; max?: string; }>;
}

// Response for auditing orders endpoint (paged rows + filter catalogs)
export interface AuditingOrdersResponse {
    orders: AuditingOrderItem[];
    filterOptions: {
        insurances: Array<{ id: string; name: string; }>;
        makes: string[];
        locations: Array<{ id: string; name: string; }>;
    };
}

// Response for auditing parts endpoint (paged rows + filter catalogs)
export interface AuditingPartsResponse {
    parts: AuditingPartItem[];
    totalCount: number;
    totalPages: number;
    page: number;
    pageSize: number;
    filterOptions: {
        insurances: Array<{ id: string; name: string; }>;
        makes: string[];
        partTypes: string[];
        vendors: string[];
        locations: Array<{ id: string; name: string; }>;
    };
}

export interface AuditingStatsResponse {
    automation: AutomationStats;
    automationByLocation: AutomationByLocation[];
    tableData?: OrderAuditItem[] | PartAuditItem[];
    viewMode: 'orders' | 'parts';
    totalCount: number;
}

// ===================
// COMBO CHARTS (with domain filtering)
// ===================

export interface ComboChartRequest extends BaseReportingRequest {
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    metrics: MetricType[];
    primaryDomain?: DomainType;
    primaryDomainId?: string;
    secondaryDomain?: DomainType;
    preset?: string;
}

export interface ComboDataPoint {
    date: string;
    domainId?: string;
    domainName?: string;
    [key: string]: string | number | undefined;
}

export interface ComboChartResponse {
    data: ComboDataPoint[];
    metrics: MetricType[];
    granularity: string;
    primaryDomain?: DomainType;
    secondaryDomain?: DomainType;
}

// ===================
// LEGACY TYPES (for backwards compatibility)
// ===================

export interface LocationStat extends DomainStatItem { }
export interface VendorStat extends DomainStatItem { }

export interface LocationStatsResponse {
    locations: LocationStat[];
    totals: DomainStatsTotals;
}

export interface VendorStatsResponse {
    vendors: VendorStat[];
    totals: DomainStatsTotals;
}

// Auditing Bookmarks
export type AuditingBookmarkTargetType = 'order' | 'part';
export type AuditingBookmarkType = 'saved' | 'flagged';

export interface AuditingBookmark {
    id: string;
    userId: string;
    targetId: string;        // Order ID or Part ID
    orderId: string;         // Always store orderId for navigation
    targetType: AuditingBookmarkTargetType;
    bookmarkType: AuditingBookmarkType;
    notes?: string;
    createdAt: string;
    updatedAt: string;
    // Denormalized data for display in Set Aside view
    roNumber?: string;
    vehicleMake?: string;
    insuranceName?: string;
    partName?: string;       // Only for parts
}

export interface GetAuditingBookmarksRequest {
    targetType?: AuditingBookmarkTargetType;
    bookmarkType?: AuditingBookmarkType;
}

export interface GetAuditingBookmarksResponse {
    bookmarks: AuditingBookmark[];
}

export interface CreateAuditingBookmarkRequest {
    targetId: string;
    orderId: string;
    targetType: AuditingBookmarkTargetType;
    bookmarkType: AuditingBookmarkType;
    notes?: string;
    // Denormalized data
    roNumber?: string;
    vehicleMake?: string;
    insuranceName?: string;
    partName?: string;
}

export interface CreateAuditingBookmarkResponse {
    bookmark: AuditingBookmark;
}

export interface DeleteAuditingBookmarkRequest {
    targetId: string;
    targetType: AuditingBookmarkTargetType;
    bookmarkType: AuditingBookmarkType;
}

// ===================
// PRODUCTIVITY STATS (By-person performance)
// ===================

export interface ProductivityStatsRequest extends BaseReportingRequest {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface ProductivityLocationBreakdown {
    orderCount: number;
    orderVolume: number;
    quoteCount: number;
    totalMissedOpportunity: number;
    totalParts: number;
    incorrectPartNumbers: number;
    manuallyFlaggedParts: number;
    totalFlaggedParts: number;
    pctPartsFlagged: number;
}

export interface ProductivityLocationDetailBreakdown extends ProductivityLocationBreakdown {
    locationId: string;
    locationName: string;
}

export interface ProductivityStatItem {
    userId: string;
    firstName: string;
    lastName: string;
    position?: string;
    quoteCount: number;
    quotesPerOrder: number;
    orderCount: number;
    orderVolume: number;
    incorrectPartNumbers: number;
    partsWithoutPartNumber: number;
    manuallyFlaggedParts: number;
    totalFlaggedParts: number;
    manualOverrides: number;
    pctPartsFlagged: number;
    totalMissedOpportunity: number;
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;
    totalParts: number;
    pctMissedOpportunity: number;
    oemEndorsementCount: number;
    expeditedCount: number;
    dealerReferralCount: number;
    quotesOnlyCount: number;
    potentialTotalCount: number;
    orderedOrders?: Array<{ orderId: string; roNumber: string; }>;
    quotesOnlyOrders?: Array<{ orderId: string; roNumber: string; }>;
    potentialTotalOrders?: Array<{ orderId: string; roNumber: string; }>;
    partsWithoutPartNumberOrders?: Array<{ orderId: string; roNumber: string; }>;
    myLocationsBreakdown?: ProductivityLocationBreakdown;
    myLocationsPerLocationBreakdown?: ProductivityLocationDetailBreakdown[];
    elsewhereBreakdown?: ProductivityLocationBreakdown;
    /** PostHog screen time — only present for admins. Surfaced as two columns
     * (Daily / Weekly) with an aggregation dropdown selecting among these. */
    dailyAvgMinutes?: number;
    dailyMedianMinutes?: number;
    weeklyTotalMinutes?: number;
    weeklyAvgMinutes?: number;
    weeklyMedianMinutes?: number;
    prior?: Partial<Omit<ProductivityStatItem, 'userId' | 'firstName' | 'lastName' | 'prior' | 'change'>>;
    change?: ComparisonChange;
}

export interface ProductivityStatsTotals {
    quoteCount: number;
    quotesPerOrder: number;
    orderCount: number;
    orderVolume: number;
    incorrectPartNumbers: number;
    partsWithoutPartNumber: number;
    manuallyFlaggedParts: number;
    totalFlaggedParts: number;
    manualOverrides: number;
    pctPartsFlagged: number;
    totalMissedOpportunity: number;
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;
    totalParts: number;
    pctMissedOpportunity: number;
    oemEndorsementCount: number;
    expeditedCount: number;
    dealerReferralCount: number;
    quotesOnlyCount: number;
    potentialTotalCount: number;
    change?: ComparisonChange;
}

export interface ProductivityInactiveUser {
    userId: string;
    firstName: string;
    lastName: string;
    position?: string;
}

export interface ProductivityStatsResponse {
    items: ProductivityStatItem[];
    totals: ProductivityStatsTotals;
    /** Users with zero quotes in range; omit/empty until API ships. */
    inactiveUsers?: ProductivityInactiveUser[];
}

// ============================================================================
// Pivot Table
// ============================================================================

export type PivotDomain = 'location' | 'insurance' | 'vendor' | 'oem' | 'estimator';

/** @deprecated Use PivotDomain instead */
export type SecondaryDomain = 'insurance' | 'vendor' | 'oem';

export interface PivotStatsRequest {
    startDate: string;
    endDate: string;
    primaryDomain?: PivotDomain;
    secondaryDomain: PivotDomain;
    excludeEvs?: boolean;
}

export interface PivotCellMetrics {
    orderCount: number;
    orderVolume: number;
    orderIds?: string[];
    avgOrderSize: number;
    medianOrderSize: number;
    avgGrossMarginPct: number;
    medianGrossMarginPct: number;
    avgGrossMarginPctWoPM: number;
    partListPricePctOfOE: number;
    missedOpportunity: number;
    avgMissedOpportunity: number;
    medianMissedOpportunity: number;
}

export interface PivotStatsResponse {
    rows: { id: string; name: string; }[];
    columns: { id: string; name: string; }[];
    cells: Record<string, Record<string, PivotCellMetrics>>;
    rowTotals: Record<string, PivotCellMetrics>;
    columnTotals: Record<string, PivotCellMetrics>;
    grandTotal: PivotCellMetrics;
}

// ============================================================================
// Inventory reporting (Auditing → Inventory)
// ============================================================================

export type InventoryReportingAdjustmentType = 'order' | 'usage' | 'sale' | 'initial';

export type InventoryReportingExportFormat = 'csv' | 'xlsx';

export type InventoryReportingExportColumnKey =
    | 'name'
    | 'manufacturerPartNumber'
    | 'locationTitle'
    | 'supplierName'
    | 'category'
    | 'unit'
    | 'bins'
    | 'tags'
    | 'partType'
    | 'businessLine'
    | 'adjustmentType'
    | 'quantity'
    | 'orderedAndInitialQuantity'
    | 'usedQuantity'
    | 'soldQuantity'
    | 'unitCost'
    | 'lineCost'
    | 'inStockQuantityAfter'
    | 'timestamp'
    | 'roNumber'
    | 'note'
    | 'targetQuantity'
    | 'reorderQuantity';

export interface InventoryReportingExportColumn {
    key: InventoryReportingExportColumnKey;
    label: string;
}

export interface InventoryReportingChangesRequest {
    startDate: string;
    endDate: string;
    locationIds?: string[];
    preset?: string;
    types?: InventoryReportingAdjustmentType[];
    tagIds?: string[];
    binIds?: string[];
    partTypes?: InventoryPartType[];
    supplierIds?: string[];
    search?: string;
    limit?: number;
    offset?: number;
}

export interface InventoryReportingExportRequest {
    startDate: string;
    endDate: string;
    locationIds?: string[];
    preset?: string;
    types?: InventoryReportingAdjustmentType[];
    tagIds?: string[];
    binIds?: string[];
    partTypes?: InventoryPartType[];
    supplierIds?: string[];
    search?: string;
    format: InventoryReportingExportFormat;
    columns: InventoryReportingExportColumn[];
}

export interface InventoryReportingTotals {
    adjustmentCount: number;
    partCount: number;
    orderedQuantity: number;
    usedQuantity: number;
    soldQuantity: number;
    initialQuantity: number;
    netQuantity: number;
    orderedCost: number;
    usedCost: number;
    soldCost: number;
    initialCost: number;
    netCost: number;
    totalSales: number;
}

/** Flat adjustment row with joined inventory item fields (Auditing Inventory table). */
export interface InventoryReportingAdjustmentRow {
    _id: string;
    type: InventoryReportingAdjustmentType;
    quantity: number;
    unitCost?: number;
    lineCost: number;
    businessLine: string;
    roNumber?: string;
    /** Optional line note; omit when empty. */
    note?: string;
    inStockQuantityAfter: number;
    timestamp: string;
    userName: { firstName: string; lastName: string } | null;
    inventoryItemId: string;
    name: string;
    manufacturerPartNumber: string;
    category: 'part' | 'material' | 'liquid';
    unit: InventoryUnit;
    bins: Array<{ _id: string; name: string }>;
    tags: Array<{ _id: string; name: string }>;
    locationId: string;
    locationTitle: string;
    supplierId: string;
    supplierName: string;
    partType: InventoryPartType;
    targetQuantity: number | null;
    reorderQuantity: number | null;
}

export interface InventoryReportingChangesResponse {
    totals: InventoryReportingTotals;
    adjustments: InventoryReportingAdjustmentRow[];
    totalCount: number;
}

export type InventoryReportingExportResponse =
    | { csv: string; xlsx?: never }
    | { xlsx: string; csv?: never };

// ============================================================================
// Inventory metrics dashboard (Reporting → Inventory)
// ============================================================================

/** Shared filters for `/reporting/inventory/summary` and `/timeseries`. */
export interface InventoryMetricsReportingRequest {
    startDate: string;
    endDate: string;
    locationIds?: string[];
    preset?: string;
    types?: InventoryReportingAdjustmentType[];
    tagIds?: string[];
    binIds?: string[];
    partTypes?: InventoryPartType[];
    supplierIds?: string[];
    search?: string;
    /** When set, only items with this restockAlert value are included. */
    restockAlert?: boolean;
    /**
     * When true, send a valid `comparison`. Otherwise omit / set false
     * (e.g. custom date ranges — no prior / change).
     */
    includeComparison?: boolean;
    comparison?: ReportingComparison;
}

/** Per-unit vs per-item sample for inventory average / median (api-changelog). */
export type InventorySampleType = 'perUnit' | 'perItem';

export interface InventorySampleAggregates {
    perUnit: number;
    perItem: number;
}

/** Total + average + median for a single inventory metric (one-shot summary/timeseries). */
export interface InventoryMetricAggregates {
    total: number;
    average: InventorySampleAggregates;
    median: InventorySampleAggregates;
}

export interface InventorySampleChange {
    perUnit?: number;
    perItem?: number;
}

export interface InventoryMetricChange {
    total?: number;
    average?: InventorySampleChange;
    median?: InventorySampleChange;
}

export type InventoryAggregatedMetricKey =
    | 'inStockValue'
    | 'inStockQuantity'
    | 'zeroCostStockQuantity'
    | 'orderedQuantity'
    | 'orderedValue'
    | 'initialQuantity'
    | 'initialValue'
    | 'usedQuantity'
    | 'usedValue'
    | 'soldQuantity'
    | 'salesValue'
    | 'inboundQuantity'
    | 'inboundValue'
    | 'outboundQuantity'
    | 'outboundValue';

export type InventoryMetricsChange = Partial<
    Record<InventoryAggregatedMetricKey, InventoryMetricChange>
> & {
    targetQuantitySum?: number;
    reorderQuantitySum?: number;
};

export interface InventoryMetricsTagLocationRow {
    tagId: string;
    tagName: string;
    locationId: string;
    locationTitle: string;
    inStockValue: InventoryMetricAggregates;
    inStockQuantity: InventoryMetricAggregates;
    zeroCostStockQuantity: InventoryMetricAggregates;
    inboundValue: InventoryMetricAggregates;
    usedValue: InventoryMetricAggregates;
    salesValue: InventoryMetricAggregates;
    orderedValue: InventoryMetricAggregates;
    initialValue: InventoryMetricAggregates;
}

/** Numeric metrics shared by summary current / prior payloads. */
export interface InventoryMetricsSummaryMetrics {
    inStockValue: InventoryMetricAggregates;
    inStockQuantity: InventoryMetricAggregates;
    zeroCostStockQuantity: InventoryMetricAggregates;
    orderedQuantity: InventoryMetricAggregates;
    orderedValue: InventoryMetricAggregates;
    initialQuantity: InventoryMetricAggregates;
    initialValue: InventoryMetricAggregates;
    usedQuantity: InventoryMetricAggregates;
    usedValue: InventoryMetricAggregates;
    soldQuantity: InventoryMetricAggregates;
    salesValue: InventoryMetricAggregates;
    inboundQuantity: InventoryMetricAggregates;
    inboundValue: InventoryMetricAggregates;
    outboundQuantity: InventoryMetricAggregates;
    outboundValue: InventoryMetricAggregates;
    /** Current item-config sum (not historical). */
    targetQuantitySum: number;
    /** Current item-config sum (not historical). */
    reorderQuantitySum: number;
}

export interface InventoryMetricsSummaryRequest extends InventoryMetricsReportingRequest {
    includeTagLocationBreakdown?: boolean;
}

export interface InventoryMetricsSummaryResponse extends InventoryMetricsSummaryMetrics {
    byTagLocation?: InventoryMetricsTagLocationRow[];
    prior?: InventoryMetricsSummaryMetrics;
    change?: InventoryMetricsChange;
}

export interface InventoryActivitySegment {
    usedQuantity: InventoryMetricAggregates;
    usedValue: InventoryMetricAggregates;
    soldQuantity: InventoryMetricAggregates;
    salesValue: InventoryMetricAggregates;
    orderedQuantity: InventoryMetricAggregates;
    orderedValue: InventoryMetricAggregates;
    initialQuantity: InventoryMetricAggregates;
    initialValue: InventoryMetricAggregates;
}

export type InventoryLocationSegment = InventoryActivitySegment & {
    inboundQuantity: InventoryMetricAggregates;
    outboundQuantity: InventoryMetricAggregates;
};

export interface InventoryReportingTimeseriesDataPoint {
    date: string;
    priorDate?: string;
    priorEndDate?: string;
    orderedQuantity: InventoryMetricAggregates;
    orderedValue: InventoryMetricAggregates;
    initialQuantity: InventoryMetricAggregates;
    initialValue: InventoryMetricAggregates;
    usedQuantity: InventoryMetricAggregates;
    usedValue: InventoryMetricAggregates;
    soldQuantity: InventoryMetricAggregates;
    salesValue: InventoryMetricAggregates;
    inboundQuantity: InventoryMetricAggregates;
    inboundValue: InventoryMetricAggregates;
    outboundQuantity: InventoryMetricAggregates;
    outboundValue: InventoryMetricAggregates;
    inStockQuantity: InventoryMetricAggregates;
    inStockValue: InventoryMetricAggregates;
    zeroCostStockQuantity: InventoryMetricAggregates;
    byBusinessLine: Record<string, InventoryActivitySegment>;
    byCategory: {
        part: InventoryActivitySegment;
        material: InventoryActivitySegment;
        liquid: InventoryActivitySegment;
    };
    byLocation: Record<string, InventoryLocationSegment>;
    prior?: Omit<
        InventoryReportingTimeseriesDataPoint,
        'date' | 'priorDate' | 'priorEndDate' | 'prior'
    >;
}

export interface InventoryMetricsTimeseriesRequest extends InventoryMetricsReportingRequest {
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
}

export interface InventoryMetricsTimeseriesResponse {
    data: InventoryReportingTimeseriesDataPoint[];
    priorData?: InventoryReportingTimeseriesDataPoint[];
    granularity: string;
    comparison?: ReportingComparison;
    locations: Array<{ id: string; name: string }>;
    /** Current item-config sums for In-Stock dashed reference lines (not historical). */
    targetQuantitySum: number;
    reorderQuantitySum: number;
}

/** Live stock $ for Inventory list tab (`POST /inventory/summary`). */
export interface InventoryLiveSummaryRequest {
    statusFilter: 'active' | 'flagged' | 'discontinued';
    locationIds: string[] | 'all';
    search?: string;
    additionalFilters?: {
        partType?: InventoryPartType[];
        category?: Array<'part' | 'material' | 'liquid'>;
        dateAddedSort?: 'asc' | 'desc' | 'range';
        dateAddedRange?: { start: string; end: string };
        tagIds?: string[];
        binIds?: string[];
    };
}

export interface InventoryLiveSummaryResponse {
    /** Live Σ runningOrderCost over filtered items (renamed from totalInventoryValue). */
    inStockValue: number;
}
